"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, Bot, Search, Cpu, Layers, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const pillars = [{
  icon: Search,
  title: "Semantic Entity Optimization",
  description: "I map your brand's topical authority, knowledge graphs, and structured schema so AI engines accurately interpret your core entities."
}, {
  icon: Cpu,
  title: "AI Overviews & Search Alignment",
  description: "I optimize your high-intent content to serve as authoritative citation sources for Google AI Overviews and answer engines."
}, {
  icon: Layers,
  title: "Information Gain & Depth",
  description: "I restructure content strategies around first-hand insights and proprietary data that generic AI summaries cannot replicate."
}, {
  icon: ShieldCheck,
  title: "Technical Foundation & Crawlability",
  description: "I refine rendering, bot access, and server-side responses to ensure search bots and generative crawlers index your assets cleanly."
}];
const principles = ["No false promises of 'guaranteed AI rankings' — I focus on proven citation readiness.", "Direct strategy and implementation conducted personally by me, not handed to junior staff.", "Balanced optimization preserving immediate organic revenue while building future-proof visibility.", "Data-grounded reporting tracking both classic rankings and evolving AI overview citations."];
export default function AiSearch() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="aisearchreadiness" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28 text-foreground">
      {/* Decorative ambient background accents */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-secondary/60 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex justify-center">
            <Badge variant="outline" className="gap-1.5 border-primary/20 bg-secondary/50 px-3 py-1 font-serif text-xs font-semibold uppercase tracking-wider text-primary">
              <Sparkles className="size-3.5 text-accent" />
              AI Search Readiness
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.1,
          ease: "easeOut"
        }} className="mt-4 font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Preparing Your Brand for{" "}
            <span className="text-primary">Google Overviews & AI Search</span>
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.2,
          ease: "easeOut"
        }} className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Search is evolving beyond simple link lists. I help forward-thinking
            businesses build authoritative digital footprints that search engines
            and large language models trust, cite, and reference directly.
          </motion.p>
        </div>

        {/* Feature Cards Grid */}
        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.title} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: shouldReduceMotion ? 0 : i * 0.1,
            ease: "easeOut"
          }} className="h-full">
                <Card className="group h-full border border-border bg-card transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
                  <CardHeader className="space-y-4">
                    <div className="flex size-12 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-xs transition-colors duration-300 group-hover:bg-accent group-hover:text-accent-foreground">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="font-serif text-lg font-bold text-card-foreground">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {pillar.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Bottom Consultation & Transparency Banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3,
        ease: "easeOut"
      }} className="mt-12 overflow-hidden rounded-2xl border border-border bg-card p-8 shadow-sm md:p-12">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
            <div className="space-y-4 lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-foreground">
                <Bot className="size-4 text-primary" />
                <span>My Pragmatic AI Approach</span>
              </div>
              <h3 className="font-serif text-2xl font-bold text-card-foreground sm:text-3xl">
                Honest Strategy. No AI Hype.
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                I do not sell automated gimmicks or promise guaranteed placements in generative AI summaries. Instead, I align your technical structure and content depth with how modern search engines actually evaluate truth and authority.
              </p>

              <Separator className="my-4" />

              <ul className="space-y-2.5">
                {principles.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-2.5 text-xs text-muted-foreground sm:text-sm">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="flex flex-col items-start justify-center gap-4 rounded-xl border border-border/80 bg-background p-6 lg:col-span-5">
              <div className="flex items-center gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary text-primary">
                  <HelpCircle className="size-5" />
                </div>
                <div>
                  <h4 className="font-serif text-sm font-bold text-foreground">
                    Curious About Your AI Footprint?
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Get a clear appraisal of how search engines see your brand.
                  </p>
                </div>
              </div>

              <p className="text-xs leading-relaxed text-muted-foreground">
                During our initial consultation, I will review your site&apos;s current entity clarity and search visibility.
              </p>

              <div className="w-full pt-2">
                <Link to="/AISearchOptimisationPage" className="w-full">
                  <Button size="lg" className="w-full justify-between bg-primary text-primary-foreground hover:bg-primary/90">
                    <span>Explore AI Search Service</span>
                    <ArrowRight className="size-4" />
                  </Button>
                </Link>
                <div className="mt-2 text-center">
                  <Link to="/ContactPage" className="text-xs font-medium text-muted-foreground hover:text-primary transition-colors">
                    Or book a direct consultation with me &rarr;
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}